import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, FileSearch, FileText, Send, ShieldCheck,
  Check, Clock, AlertTriangle, ChevronDown, ChevronUp,
  Pencil, Save, Loader2
} from 'lucide-react';
import { Button } from '../../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const NOTE_TYPES = {
  file_notes: { label: 'File Notes', icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
  submission_notes: { label: 'Submission Notes', icon: Send, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
  deal_qa: { label: 'Deal QA', icon: ShieldCheck, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
};

export default function AiTab({ deal, dealId, onRefresh }) {
  const [configured, setConfigured] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [extractResults, setExtractResults] = useState(null);
  const [expandedNote, setExpandedNote] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [editContent, setEditContent] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, notesRes] = await Promise.all([
        axios.get(`${API}/crm/ai/status`),
        axios.get(`${API}/crm/ai/notes?deal_id=${dealId}`),
      ]);
      setConfigured(statusRes.data.configured);
      setAiStatus(statusRes.data);
      setNotes(notesRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dealId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExtract = async () => {
    setExtracting(true);
    setExtractResults(null);
    try {
      const res = await axios.post(`${API}/crm/ai/extract-docs?deal_id=${dealId}`);
      setExtractResults(res.data.results);
      const extracted = res.data.results.filter(r => r.status === 'extracted').length;
      const skipped = res.data.results.filter(r => r.status === 'already_extracted').length;
      toast.success(`${extracted} extracted, ${skipped} already done`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Extraction failed'); }
    finally { setExtracting(false); }
  };

  const handleGenerate = async (type) => {
    if (!configured) { toast.error('AI is not configured in this environment'); return; }
    setGenerating(type);
    try {
      const endpoint = type === 'file_notes' ? 'generate-file-notes' :
                       type === 'submission_notes' ? 'generate-submission-notes' : 'deal-qa';
      const res = await axios.post(`${API}/crm/ai/${endpoint}?deal_id=${dealId}`);
      toast.success(`${NOTE_TYPES[type].label} generated`);
      setNotes(prev => [res.data, ...prev]);
      setExpandedNote(res.data.id);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      toast.error(detail || 'AI generation failed');
    }
    finally { setGenerating(null); }
  };

  const handleApprove = async (noteId) => {
    try {
      await axios.post(`${API}/crm/ai/notes/${noteId}/approve`);
      toast.success('Note approved');
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, status: 'approved' } : n));
    } catch (e) { toast.error('Failed to approve'); }
  };

  const handleSaveEdit = async (noteId) => {
    try {
      await axios.put(`${API}/crm/ai/notes/${noteId}`, { content: editContent });
      toast.success('Note updated');
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, content: editContent, version: (n.version || 1) + 1 } : n));
      setEditingNote(null);
    } catch (e) { toast.error('Failed to save'); }
  };

  const stripCitations = (content) => {
    if (!content) return '';
    const idx = content.indexOf('```citations');
    return idx > -1 ? content.substring(0, idx).trim() : content;
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div></div>;
  }

  if (configured === false) {
    return (
      <div className="max-w-3xl" data-testid="ai-tab">
        <div className="text-center py-16 space-y-4">
          <Sparkles className="h-12 w-12 text-gray-600 mx-auto" />
          <h2 className="text-white font-medium text-lg">AI Not Configured</h2>
          <p className="text-gray-400 text-sm max-w-md mx-auto">
            AI features require an API key. Set <code className="text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded text-xs">ANTHROPIC_API_KEY</code> in your environment to enable Claude-powered deal analysis, notes generation, and QA reports.
          </p>
          {notes.length > 0 && (
            <p className="text-gray-500 text-xs">Previously generated notes are still available below.</p>
          )}
        </div>
        {notes.length > 0 && (
          <div className="space-y-3 mt-4">
            {notes.map(note => {
              const cfg = NOTE_TYPES[note.type] || NOTE_TYPES.file_notes;
              const NoteIcon = cfg.icon;
              return (
                <div key={note.id} className={`rounded-xl border p-4 ${cfg.bg}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <NoteIcon className={`h-4 w-4 ${cfg.color}`} />
                    <span className="text-white text-sm font-medium">{cfg.label}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${note.status === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>{note.status}</span>
                  </div>
                  <div className="text-gray-400 text-sm whitespace-pre-wrap line-clamp-4">{stripCitations(note.content).slice(0, 300)}...</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Get latest note per type
  const latestByType = {};
  notes.forEach(n => {
    if (!latestByType[n.type]) latestByType[n.type] = n;
  });

  return (
    <div className="max-w-4xl space-y-5" data-testid="ai-tab">
      {/* Header + Status */}
      <div className="flex items-center justify-between">
        <h2 className="text-white font-medium flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-400" /> AI Assistant
        </h2>
        {aiStatus && (
          <div className="flex items-center gap-3 text-xs" data-testid="ai-status-bar">
            <span className={`flex items-center gap-1 ${aiStatus.provider_ready ? 'text-green-400' : 'text-red-400'}`}>
              <span className={`w-2 h-2 rounded-full ${aiStatus.provider_ready ? 'bg-green-400' : 'bg-red-400'}`}></span>
              {aiStatus.provider_ready ? 'Ready' : 'Not configured'}
            </span>
            <span className="text-gray-500">Backend: <span className="text-gray-400">{aiStatus.backend}</span></span>
            <span className="text-gray-500">Anthropic: <span className={aiStatus.has_anthropic_key ? 'text-green-400' : 'text-gray-600'}>{aiStatus.has_anthropic_key ? 'Yes' : 'No'}</span></span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ActionButton
          icon={<FileSearch className="h-5 w-5" />}
          label="Extract Docs"
          sublabel="Read uploaded PDFs"
          onClick={handleExtract}
          loading={extracting}
          color="teal"
          testId="ai-extract-docs-btn"
        />
        <ActionButton
          icon={<FileText className="h-5 w-5" />}
          label="File Notes"
          sublabel="Internal analysis"
          onClick={() => handleGenerate('file_notes')}
          loading={generating === 'file_notes'}
          color="blue"
          testId="ai-file-notes-btn"
          badge={latestByType.file_notes?.status}
        />
        <ActionButton
          icon={<Send className="h-5 w-5" />}
          label="Submission Notes"
          sublabel="Lender-facing"
          onClick={() => handleGenerate('submission_notes')}
          loading={generating === 'submission_notes'}
          color="purple"
          testId="ai-submission-notes-btn"
          badge={latestByType.submission_notes?.status}
        />
        <ActionButton
          icon={<ShieldCheck className="h-5 w-5" />}
          label="Deal QA"
          sublabel="Gaps & risks"
          onClick={() => handleGenerate('deal_qa')}
          loading={generating === 'deal_qa'}
          color="amber"
          testId="ai-deal-qa-btn"
          badge={latestByType.deal_qa?.status}
        />
      </div>

      {/* Extraction Results */}
      {extractResults && (
        <div className="bg-navy-800/50 rounded-xl p-4 border border-teal-500/20" data-testid="extract-results">
          <h4 className="text-teal-400 text-xs uppercase tracking-wider font-medium mb-2">Extraction Results</h4>
          <div className="space-y-1">
            {extractResults.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                {r.status === 'extracted' ? <Check className="h-3 w-3 text-green-400" /> :
                 r.status === 'already_extracted' ? <Check className="h-3 w-3 text-gray-500" /> :
                 <AlertTriangle className="h-3 w-3 text-yellow-400" />}
                <span className="text-gray-300">{r.filename}</span>
                <span className="text-gray-500">
                  {r.status === 'extracted' ? `${r.pages} pages` :
                   r.status === 'already_extracted' ? 'already done' :
                   r.status}
                </span>
                {r.ocr_required && <span className="text-yellow-400 text-[10px] bg-yellow-500/10 px-1.5 rounded">OCR needed</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes List */}
      {notes.length === 0 && !extractResults && (
        <div className="text-center py-8 text-gray-500 text-sm">
          No AI notes yet. Extract documents first, then generate notes.
        </div>
      )}

      {notes.map(note => {
        const cfg = NOTE_TYPES[note.type] || NOTE_TYPES.file_notes;
        const NoteIcon = cfg.icon;
        const isExpanded = expandedNote === note.id;
        const isEditing = editingNote === note.id;
        const displayContent = stripCitations(note.content);

        return (
          <div key={note.id} className={`rounded-xl border overflow-hidden ${cfg.bg}`} data-testid={`ai-note-${note.id}`}>
            <button
              onClick={() => { setExpandedNote(isExpanded ? null : note.id); setEditingNote(null); }}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition"
              data-testid={`expand-note-${note.id}`}
            >
              <div className="flex items-center gap-3 flex-wrap">
                <NoteIcon className={`h-4 w-4 ${cfg.color}`} />
                <span className="text-white font-medium text-sm">{cfg.label}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  note.status === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {note.status === 'approved' ? 'Approved' : 'Draft'}
                </span>
                <span className="text-xs text-gray-500">v{note.version || 1}</span>
                <span className="text-xs text-gray-600">{new Date(note.created_at).toLocaleString()}</span>
              </div>
              {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
            </button>

            {isExpanded && (
              <div className="px-4 pb-4 border-t border-white/5">
                {/* Actions */}
                <div className="flex gap-2 mt-3 mb-4">
                  {note.status !== 'approved' && (
                    <Button onClick={() => handleApprove(note.id)} size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-note-${note.id}`}>
                      <Check className="h-3.5 w-3.5 mr-1" />Approve
                    </Button>
                  )}
                  {!isEditing ? (
                    <Button onClick={() => { setEditingNote(note.id); setEditContent(note.content); }}
                      size="sm" variant="outline" className="border-white/10 text-gray-300" data-testid={`edit-note-${note.id}`}>
                      <Pencil className="h-3.5 w-3.5 mr-1" />Edit
                    </Button>
                  ) : (
                    <Button onClick={() => handleSaveEdit(note.id)} size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`save-note-${note.id}`}>
                      <Save className="h-3.5 w-3.5 mr-1" />Save
                    </Button>
                  )}
                </div>

                {/* Content */}
                {isEditing ? (
                  <textarea
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    rows={20}
                    className="w-full bg-navy-900 border border-white/10 rounded-lg p-3 text-sm text-gray-300 font-mono focus:border-purple-500 focus:outline-none"
                    data-testid={`edit-textarea-${note.id}`}
                  />
                ) : (
                  <div className="prose prose-invert prose-sm max-w-none text-gray-300 text-sm leading-relaxed whitespace-pre-wrap" data-testid={`note-content-${note.id}`}>
                    {displayContent}
                  </div>
                )}

                {/* Citations */}
                {note.citations && note.citations.length > 0 && !isEditing && (
                  <div className="mt-4 border-t border-white/5 pt-3">
                    <h5 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Citations ({note.citations.length})</h5>
                    <div className="space-y-1">
                      {note.citations.map((c, i) => (
                        <div key={i} className="text-xs text-gray-500 flex items-start gap-2">
                          <span className="text-gray-600 shrink-0">[{i + 1}]</span>
                          <span>
                            {c.source_type === 'document'
                              ? `${c.filename || c.doc_id}${c.page ? `, Page ${c.page}` : ''}`
                              : c.form_field || 'Form data'}
                            {c.snippet && <span className="text-gray-600 italic"> — "{c.snippet}"</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function ActionButton({ icon, label, sublabel, onClick, loading, color, testId, badge }) {
  const colors = {
    teal: 'border-teal-500/20 hover:border-teal-500/40 text-teal-400',
    blue: 'border-blue-500/20 hover:border-blue-500/40 text-blue-400',
    purple: 'border-purple-500/20 hover:border-purple-500/40 text-purple-400',
    amber: 'border-amber-500/20 hover:border-amber-500/40 text-amber-400',
  };

  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`bg-navy-800/50 border rounded-xl p-4 text-left transition hover:bg-white/5 ${colors[color] || colors.teal} ${loading ? 'opacity-60' : ''}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : icon}
        {badge && (
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
            badge === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
          }`}>{badge}</span>
        )}
      </div>
      <p className="text-white text-sm font-medium">{label}</p>
      <p className="text-gray-500 text-[10px]">{sublabel}</p>
    </button>
  );
}
